import { useMemo } from 'react';
import styles from './style.module.scss';
import { useTranslation } from 'react-i18next';
import { Namespaces } from 'types/locales';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import type { InsightsResponse } from '@zoocasa/go-search';

export interface AreaFaqProps {
  /** Display name of the area, e.g. "Toronto". */
  areaName?: string;
  /** Live count of listings currently matching the area search. */
  listingCount?: number;
  /** Market insights for the area (median price, DOM, sell/list ratio, etc.). */
  insightsData?: InsightsResponse;
}

type Faq = { question: string; answer: string };

/**
 * English fallbacks for every FAQ key. SSR in this app cannot rely on the
 * loaded i18n store (localePath resolves relative to the dev server cwd), so
 * — following the repo convention — every t() call passes a defaultValue.
 * This keeps SSR and client output identical and prevents hydration mismatches.
 */
const EN_DEFAULTS: Record<string, string> = {
  'areaFaq.heading': '{{area}} Real Estate FAQ',
  'areaFaq.label_detached': 'detached homes',
  'areaFaq.label_townhomes': 'townhomes',
  'areaFaq.label_condos': 'condos',
  'areaFaq.label_rentals': 'rentals',
  'areaFaq.q1_question': 'What is the average home price in {{area}}?',
  'areaFaq.q1_answer': 'The median home price in {{area}} is {{median}} based on sales over the past 90 days.',
  'areaFaq.q1_breakdown': 'By property type, average prices are: {{list}}.',
  'areaFaq.q2_question': 'How many homes are for sale in {{area}} right now?',
  'areaFaq.q2_answer': 'There are currently {{total}} homes for sale in {{area}}. This count updates in real time as listings are added and sold.',
  'areaFaq.q3_question': "Is {{area}} a buyer's or seller's market?",
  'areaFaq.q3_answer_seller': "{{area}} is currently a seller's market, with homes selling at about {{pct}}% of their list price on average.",
  'areaFaq.q3_answer_buyer': "{{area}} is currently a buyer's market, with homes selling at about {{pct}}% of their list price on average.",
  'areaFaq.q3_answer_balanced': '{{area}} is currently a balanced market, with homes selling at about {{pct}}% of their list price on average.',
  'areaFaq.q3_dom_clause': 'Homes take about {{dom}} days to sell.',
  'areaFaq.q4_question': 'What are the most popular neighbourhoods in {{area}}?',
  'areaFaq.q4_answer': 'Some of the most active neighbourhoods in {{area}} by listing volume are {{list}}.',
  'areaFaq.q5_question': 'How long do homes stay on the market in {{area}}?',
  'areaFaq.q5_answer': 'Homes in {{area}} spend an average of {{dom}} days on the market before going under contract.',
  'areaFaq.q6_question': 'What types of properties are available in {{area}}?',
  'areaFaq.q6_answer': '{{area}} offers a range of property types, including {{list}}. You can filter by type on the map above.',
  'areaFaq.q7_question': "What's driving {{area}}'s housing market this year?",
  'areaFaq.q7_answer_up': 'Over the past year, the median home price in {{area}} has risen about {{pct}}% to {{median}}, reflecting continued demand.',
  'areaFaq.q7_answer_down': 'Over the past year, the median home price in {{area}} has fallen about {{pct}}% to {{median}}, as the market cools.',
  'areaFaq.q7_answer_flat': 'Over the past year, the median home price in {{area}} has held roughly steady at {{median}}.',
  'areaFaq.q8_question': 'Where can I see current listings in {{area}} on a map?',
  'areaFaq.q8_answer': 'You can browse all {{total}} current {{area}} listings on the interactive map on this page. Use the map to explore listings by location and filter by price, property type, and more.',
};

/**
 * Area FAQ section rendered under the listings grid on hybrid area pages.
 *
 * Every answer is derived from live search / insights data — no hard-coded
 * numbers — so the copy stays accurate as the market moves. Questions whose
 * backing data is missing are skipped entirely. The same Q&A set is also
 * emitted as FAQPage JSON-LD so AI answer engines can quote it directly.
 */
const AreaFaq = ({ areaName, listingCount, insightsData }: AreaFaqProps) => {
  const { t } = useTranslation(Namespaces.COMMON);
  const { formatCurrency, formatNumber } = useI18nFormatting();

  const faqs = useMemo<Faq[]>(() => {
    // Fetch the template via t() (English defaultValue on SSR, localized on the
    // client) then interpolate {{vars}} ourselves. Server-side i18n here returns
    // defaultValue WITHOUT running interpolation, so doing it in JS guarantees
    // SSR and client produce byte-identical text — no hydration mismatch.
    const interpolate = (template: string, values?: Record<string, string>) =>
      values
        ? template.replace(/\{\{(\w+)\}\}/g, (_, k) => (k in values ? values[k] : `{{${k}}}`))
        : template;
    const tr = (key: string, values?: Record<string, string>) =>
      interpolate(t(key, { defaultValue: EN_DEFAULTS[key] }), values);

    const area = areaName?.trim();
    if (!area) {
      return [];
    }

    const items: Faq[] = [];

    const medianPrice = insightsData?.MedianPurchasePrice ?? 0;
    const avgHouse = insightsData?.AvgHousePrice ?? 0;
    const avgTown = insightsData?.AvgTownhousePrice ?? 0;
    const avgCondo = insightsData?.AvgCondoPrice ?? 0;
    const dom = insightsData?.DaysOnMarket ?? 0;
    const sellListRatio = insightsData?.SellListRatio ?? 0;
    const totalHouses = insightsData?.TotalHouses ?? 0;
    const totalTown = insightsData?.TotalTownhouses ?? 0;
    const totalCondos = insightsData?.TotalCondos ?? 0;
    const totalRentals = insightsData?.TotalRentals ?? 0;
    const neighbourhoods = insightsData?.topNeighbourhoods ?? [];
    const trends = insightsData?.medianPriceTrends ?? [];
    const count = listingCount ?? 0;

    // Q1 — average / median home price, with a per-type breakdown when available.
    if (medianPrice > 0) {
      const parts: string[] = [];
      if (avgHouse > 0) {
        parts.push(`${tr('areaFaq.label_detached')} ${formatCurrency(avgHouse)}`);
      }
      if (avgTown > 0) {
        parts.push(`${tr('areaFaq.label_townhomes')} ${formatCurrency(avgTown)}`);
      }
      if (avgCondo > 0) {
        parts.push(`${tr('areaFaq.label_condos')} ${formatCurrency(avgCondo)}`);
      }
      let answer = tr('areaFaq.q1_answer', { area, median: formatCurrency(medianPrice) });
      if (parts.length > 0) {
        answer += ' ' + tr('areaFaq.q1_breakdown', { list: parts.join(', ') });
      }
      items.push({ question: tr('areaFaq.q1_question', { area }), answer });
    }

    // Q2 — live listing count.
    if (count > 0) {
      items.push({
        question: tr('areaFaq.q2_question', { area }),
        answer: tr('areaFaq.q2_answer', { area, total: formatNumber(count) }),
      });
    }

    // Q3 — buyer's vs seller's market, proxied by sell/list ratio + days on market.
    if (sellListRatio > 0) {
      const pct = formatNumber(sellListRatio * 100, { maximumFractionDigits: 1 });
      let stateKey: 'q3_answer_seller' | 'q3_answer_buyer' | 'q3_answer_balanced';
      if (sellListRatio >= 1.0) {
        stateKey = 'q3_answer_seller';
      } else if (sellListRatio < 0.98) {
        stateKey = 'q3_answer_buyer';
      } else {
        stateKey = 'q3_answer_balanced';
      }
      const domClause = dom > 0 ? ' ' + tr('areaFaq.q3_dom_clause', { dom: formatNumber(dom) }) : '';
      items.push({
        question: tr('areaFaq.q3_question', { area }),
        answer: tr(`areaFaq.${stateKey}`, { area, pct }) + domClause,
      });
    }

    // Q4 — top neighbourhoods (no persona segmentation; not available in data).
    const topNames = neighbourhoods
      .filter(n => n?.name)
      .slice(0, 3)
      .map(n => n.name);
    if (topNames.length > 0) {
      items.push({
        question: tr('areaFaq.q4_question', { area }),
        answer: tr('areaFaq.q4_answer', { area, list: topNames.join(', ') }),
      });
    }

    // Q5 — days on market.
    if (dom > 0) {
      items.push({
        question: tr('areaFaq.q5_question', { area }),
        answer: tr('areaFaq.q5_answer', { area, dom: formatNumber(dom) }),
      });
    }

    // Q6 — property types available, with live counts.
    const typeParts: string[] = [];
    if (totalHouses > 0) {
      typeParts.push(`${formatNumber(totalHouses)} ${tr('areaFaq.label_detached')}`);
    }
    if (totalTown > 0) {
      typeParts.push(`${formatNumber(totalTown)} ${tr('areaFaq.label_townhomes')}`);
    }
    if (totalCondos > 0) {
      typeParts.push(`${formatNumber(totalCondos)} ${tr('areaFaq.label_condos')}`);
    }
    if (totalRentals > 0) {
      typeParts.push(`${formatNumber(totalRentals)} ${tr('areaFaq.label_rentals')}`);
    }
    if (typeParts.length > 0) {
      items.push({
        question: tr('areaFaq.q6_question', { area }),
        answer: tr('areaFaq.q6_answer', { area, list: typeParts.join(', ') }),
      });
    }

    // Q7 — year-over-year price trend, derived from median price history.
    const validTrends = trends.filter(point => point?.medianPrice > 0);
    if (validTrends.length >= 13) {
      const latest = validTrends[validTrends.length - 1];
      const yearAgo = validTrends[validTrends.length - 13];
      const changePct = ((latest.medianPrice - yearAgo.medianPrice) / yearAgo.medianPrice) * 100;
      const absPct = formatNumber(Math.abs(changePct), { maximumFractionDigits: 1 });
      let trendKey: 'q7_answer_up' | 'q7_answer_down' | 'q7_answer_flat';
      if (changePct >= 1) {
        trendKey = 'q7_answer_up';
      } else if (changePct <= -1) {
        trendKey = 'q7_answer_down';
      } else {
        trendKey = 'q7_answer_flat';
      }
      items.push({
        question: tr('areaFaq.q7_question', { area }),
        answer: tr(`areaFaq.${trendKey}`, { area, pct: absPct, median: formatCurrency(latest.medianPrice) }),
      });
    }

    // Q8 — where to see listings on a map (this page).
    if (count > 0) {
      items.push({
        question: tr('areaFaq.q8_question', { area }),
        answer: tr('areaFaq.q8_answer', { area, total: formatNumber(count) }),
      });
    }

    return items;
  }, [areaName, listingCount, insightsData, t, formatCurrency, formatNumber]);

  if (faqs.length === 0) {
    return null;
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const heading = t('areaFaq.heading', { defaultValue: EN_DEFAULTS['areaFaq.heading'] })
    .replace(/\{\{area\}\}/g, areaName ?? '');

  return (
    <section className={styles.component} id="area-faq" aria-label={heading}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <h2 className={styles.heading}>{heading}</h2>
      <dl className={styles.list}>
        {faqs.map((faq, i) => (
          <div key={i} className={styles.item}>
            <dt className={styles.question}>{faq.question}</dt>
            <dd className={styles.answer}>{faq.answer}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

export default AreaFaq;
